import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, Sparkles, RefreshCw, X, CornerDownLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../UI/Button';
import { albumsApi } from '../../api/albums';
import { DisplayStudio } from './DisplayStudio';
import type { Album } from '../../types';
import styles from './SlideshowSettingsTab.module.css';

// ── Password generator ───────────────────────────────────────────────────────
// Friendly 6-char passwords that avoid ambiguous characters (0/O/1/l/I).
function generateFriendlyPassword(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
  let p = '';
  for (let i = 0; i < 6; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
}

interface SlideshowSettingsTabProps {
  album: Album;
  onUpdated: (updated: Album) => void;
  /**
   * Provided when this is the entire content of a standalone modal, which
   * gives the studio its back/close affordances. Omitted when embedded as a
   * tab inside AlbumEditModal, whose own chrome already provides them.
   */
  onClose?: () => void;
}

/**
 * Two states:
 *   • not enabled → the setup flow that creates the display
 *   • enabled     → the Display Studio workspace
 */
export function SlideshowSettingsTab({ album, onUpdated, onClose }: SlideshowSettingsTabProps) {
  const isEnabled = album.slideshowEnabled ?? false;

  const initialPassword = useMemo(() => generateFriendlyPassword(), []);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(true);
  const [enabling, setEnabling] = useState(false);

  const handleEnable = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!password.trim() || password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setEnabling(true);
    try {
      const result = await albumsApi.enableSlideshow(album._id, password);
      onUpdated({ ...album, slideshowEnabled: true, slideshowToken: result.slideshowToken });
      setPassword('');
      toast.success('Venue Display enabled');
    } catch {
      toast.error('Failed to enable Venue Display');
    } finally {
      setEnabling(false);
    }
  };

  if (isEnabled) {
    return <DisplayStudio album={album} onUpdated={onUpdated} onClose={onClose} />;
  }

  const displayName = album.name.length > 28 ? `${album.name.slice(0, 26)}…` : album.name;

  return (
    <form className={styles['setup-root']} onSubmit={handleEnable} aria-labelledby="venue-display-headline">
      <div className={styles['hero']}>
        <div className={styles['hero-glow']} aria-hidden="true" />
        <div className={styles['tv-bezel']} aria-hidden="true">
          <div className={styles['tv-screen']}>
            <motion.div
              className={styles['tv-photo']}
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.05, type: 'spring', stiffness: 280, damping: 22 }}
              style={{ left: '8%', top: '14%', width: '34%', height: '52%' }}
            />
            <motion.div
              className={styles['tv-photo']}
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.18, type: 'spring', stiffness: 280, damping: 22 }}
              style={{ left: '38%', top: '22%', width: '24%', height: '56%' }}
            />
            <motion.div
              className={styles['tv-photo']}
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.32, type: 'spring', stiffness: 280, damping: 22 }}
              style={{ left: '60%', top: '18%', width: '32%', height: '52%' }}
            />
            <div className={styles['tv-live-badge']}>
              <span className={styles['tv-live-dot']} />
              LIVE
            </div>
          </div>
        </div>

        {onClose && (
          <button type="button" onClick={onClose} className={styles['hero-close']} aria-label="Close">
            <X style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>

      <div className={styles['setup-headline']}>
        <h2 id="venue-display-headline" className={styles['setup-title']}>
          Put &ldquo;{displayName}&rdquo; on the big screen
        </h2>
        <p className={styles['setup-tagline']}>
          Open one link on any TV at your venue. Photos appear in real time as your photographer captures them.
        </p>
      </div>

      <ul className={styles['benefit-list']}>
        <li className={styles['benefit-row']}>
          <span className={styles['benefit-icon']}><Lock style={{ width: 14, height: 14 }} /></span>
          <span className={styles['benefit-text']}>Password protected — only people you trust can open it</span>
        </li>
        <li className={styles['benefit-row']}>
          <span className={styles['benefit-icon']}><Sparkles style={{ width: 14, height: 14 }} /></span>
          <span className={styles['benefit-text']}>New photos appear automatically — no refresh needed</span>
        </li>
      </ul>

      <div className={styles['password-section']}>
        <label htmlFor="slideshow-password" className={styles['password-label']}>Display password</label>
        <div className={styles['password-field']}>
          <input
            id="slideshow-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 4 chars — you'll type this on the TV"
            className={styles['password-input-new']}
            autoComplete="new-password"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => setPassword(generateFriendlyPassword())}
            className={styles['password-action-chip']}
            aria-label="Generate a new password"
            title="Generate a new password"
          >
            <RefreshCw style={{ width: 13, height: 13 }} />
          </button>
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className={styles['password-action-chip']}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
          </button>
        </div>
        <p className={styles['password-hint']}>
          We&rsquo;ve suggested one — edit it or tap{' '}
          <RefreshCw style={{ width: 11, height: 11, display: 'inline', verticalAlign: '-1px' }} /> for another.
        </p>
      </div>

      <div className={styles['cta-row']}>
        <Button
          type="submit"
          variant="primary"
          loading={enabling}
          disabled={!password.trim() || password.length < 4}
          fullWidth
          aria-keyshortcuts="Enter"
        >
          Create Venue Display
          <span className={styles['cta-kbd']} aria-hidden="true">
            <CornerDownLeft style={{ width: 12, height: 12 }} />
          </span>
        </Button>
      </div>

      <p className={styles['setup-footnote']}>
        Free to disable anytime — won&rsquo;t affect your album or photos.
      </p>
    </form>
  );
}
